import {LeanDocument} from 'mongoose';
import {NotificationLike} from '@/types/NotificationLike.type';
import {ToDoItemDocument} from '@/documentTypes/to-do-manager/ToDoItem.document';
import {silentRequest} from '@/helpers/featureProbe.helper';
import {formatToDoDue, toDoDueTone} from '@/helpers/toDoDue.helper';
import {toDoEntity, ToDoRowItem} from '@/components/toDos/toDosView/toDosView.helper';
import {qualityCaseLocation, qualityCaseSourceOf} from '@/components/toDos/qualityCaseToDo.helper';
import {toDoIdOf} from '@/components/app/appBar/notifications/notificationCenter/notificationView.helper';

/*
 * To-dos behind to-do notifications (summary pop-up "N neue To-dos für dich"): preview rows and the page a to-do opens.
 * The socket payload only carries name, entity id/type and the to-do id; entity name, due date and source are loaded.
 */

type ToDoItem = LeanDocument<ToDoItemDocument>;

export type ToDoPreview = {
    toDoId: string;
    name: string;
    /** Name of the linked record (Schüler:in, Lehrkraft, Standort …), empty when unknown */
    entityName: string;
    entityIcon: string;
    /** "heute", "morgen 14:30", "Mo, 12.10." – empty without due date */
    due: string;
    dueTone: string;
    /** Router path the row opens: case in the QM-Cockpit or the to-do */
    path: string;
    /** The to-do could not be loaded (deleted, no access) */
    missing: boolean;
};

const payloadOf = (notification: NotificationLike): Record<string, unknown> => {
    const payload = (notification as { messagePayload?: unknown }).messagePayload;
    return payload && typeof payload === 'object' ? payload as Record<string, unknown> : {};
};

/** Distinct to-do ids of the notifications, in their order */
export const toDoIdsOf = (notifications: NotificationLike[]): string[] =>
    Array.from(new Set(notifications.map(toDoIdOf).filter((id): id is string => !!id)));

const QUALITY_CASE_PATH = (location: { path: string; query: { case: string } }) =>
    `${location.path}?case=${encodeURIComponent(location.query.case)}`;

/** Page of a loaded to-do: the case in the QM-Cockpit (with access) or the to-do in the to-do list */
export const toDoItemPath = (item: { _id?: unknown; source?: { type?: string } | null }): string => {
    const source = qualityCaseSourceOf(item);
    const location = source ? qualityCaseLocation(source) : null;
    return location ? QUALITY_CASE_PATH(location) : `/todos/${String(item._id)}`;
};

const fetchToDo = async (toDoId: string, withEntity: boolean): Promise<ToDoItem | undefined> => {
    const options = withEntity ? {populate: {entity: {fields: ['firstName', 'lastName', 'name']}}} : {fields: ['source']};
    const result = await silentRequest<ToDoItem>('get', 'to-do-manager', `items/${toDoId}`, options);
    return result.ok && result.data ? result.data : undefined;
};

/** Page a to-do notification opens; QM-Cockpit to-dos open their case. Falls back to the to-do when it cannot be loaded. */
export async function resolveToDoPath(toDoId: string): Promise<string> {
    const item = await fetchToDo(toDoId, false);
    return item ? toDoItemPath({...item, _id: toDoId}) : `/todos/${toDoId}`;
}

/** List of the given to-dos in the to-do page; one to-do opens directly */
export async function toDoListPath(toDoIds: string[]): Promise<string> {
    if (toDoIds.length === 1) {
        return resolveToDoPath(toDoIds[0]);
    }
    return `/todos?ids=${toDoIds.join(',')}`;
}

export async function loadToDoPreview(notification: NotificationLike): Promise<ToDoPreview | undefined> {
    const toDoId = toDoIdOf(notification);
    if (!toDoId) {
        return undefined;
    }
    const item = await fetchToDo(toDoId, true);
    if (!item) {
        return {
            toDoId, name: String(payloadOf(notification).name || 'To-do'), entityName: '', entityIcon: '', due: '',
            dueTone: 'tone-muted', path: `/todos/${toDoId}`, missing: true,
        };
    }
    const entity = item.entity ? toDoEntity(item as ToDoRowItem) : {name: '', icon: ''};
    return {
        toDoId,
        name: item.name || String(payloadOf(notification).name || 'To-do'),
        entityName: entity.name === 'Unbekannt' ? '' : entity.name,
        entityIcon: entity.icon === 'mdi-help' ? '' : entity.icon,
        due: formatToDoDue(item),
        dueTone: toDoDueTone(item),
        path: toDoItemPath({...item, _id: toDoId}),
        missing: false,
    };
}
